import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  MapPin, 
  Calendar, 
  Users, 
  Search,
  ArrowRight,
  Building2,
  Zap,
  ChevronRight,
  LayoutDashboard,
  Lock,
  Shield
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Roles allowed to view projects with prices
const ALLOWED_ROLES = ["platform_admin", "operations_manager", "team_leader", "worker"];

export default function PublicProjects() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  
  // Check if user can see prices (workers/TL/admin only, NOT clients)
  const canSeePrices = user && ALLOWED_ROLES.includes(user.role);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/public/projects`);
        setProjects(response.data);
      } catch (error) {
        console.error("Error fetching public projects:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const filteredProjects = projects.filter((project) => {
    const matchesSearch = 
      project.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.location_city?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !typeFilter || project.project_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const formatPrice = (min, max) => {
    // Only show prices to authorized roles
    if (!canSeePrices) {
      return null;
    }
    
    // If min and max are the same, show only once
    if (min && max && min === max) {
      return `${min.toLocaleString()} NOK`;
    }
    
    if (min && max) {
      return `${min.toLocaleString()} - ${max.toLocaleString()} NOK`;
    } else if (min) {
      return t('publicProjects.fromPrice', { price: min.toLocaleString() });
    } else if (max) {
      return t('publicProjects.upToPrice', { price: max.toLocaleString() });
    }
    return t('publicProjects.priceOnRequest');
  };
  
  // Redirect non-worker/TL users who aren't logged in to show access message
  const showAccessRestriction = !authLoading && (!user || !ALLOWED_ROLES.includes(user.role));

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-white/10">
        <Link to="/" className="flex items-center gap-1">
          <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
            <span className="text-[#23F7DD] font-bold text-base font-['Unbounded']">All</span>
          </div>
          <span className="text-xl font-bold font-['Unbounded'] neon-text-subtle">Stillas</span>
        </Link>

        <div className="flex items-center gap-4">
          {authLoading ? (
            <div className="w-6 h-6 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
          ) : user ? (
            <>
              <span className="text-[#94A3B8] text-sm hidden md:block">
                {t('publicProjects.welcomeUser', { name: user.name })}
              </span>
              <Link to={
                user.role === 'platform_admin' || user.role === 'operations_manager' ? '/admin' :
                user.role === 'team_leader' ? '/team-leader' :
                user.role === 'worker' ? '/worker' : '/client'
              }>
                <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold uppercase tracking-wider">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  {t('nav.dashboard')}
                </Button>
              </Link>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:text-[#23F7DD] hover:bg-white/5">
                  {t('auth.login')}
                </Button>
              </Link>
              <Link to="/register?type=client">
                <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold uppercase tracking-wider">
                  {t('publicProjects.postYourProject')}
                </Button>
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Header */}
      <section className="px-6 md:px-12 py-12 border-b border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 text-[#23F7DD] mb-4">
            <Building2 className="w-5 h-5" />
            <span className="text-sm font-medium uppercase tracking-wider">{t('publicProjects.browseProjects')}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-['Unbounded'] font-bold text-white mb-4">
            {t('publicProjects.activeProjects')}
          </h1>
          <p className="text-xl text-[#94A3B8] max-w-2xl">
            {t('publicProjects.exploreProjects')}
          </p>
        </div>
      </section>

      {/* Access Restriction Banner for non-workers */}
      {showAccessRestriction && (
        <section className="px-6 md:px-12 py-6 bg-gradient-to-r from-[#23F7DD]/10 to-[#4F46E5]/10 border-b border-[#23F7DD]/30">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#23F7DD]" />
              <div>
                <p className="text-white font-medium">{t('publicProjects.priceInfoPrivate')}</p>
                <p className="text-sm text-[#94A3B8]">
                  {t('publicProjects.onlyRegisteredScaffolders')}
                </p>
              </div>
            </div>
            <Link to="/register?type=worker">
              <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold whitespace-nowrap">
                {t('publicProjects.registerAsScaffolder')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="px-6 md:px-12 py-6 bg-[#0A0A0A] border-b border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex-1 w-full md:max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#475569]" />
              <Input
                type="text"
                placeholder={t('publicProjects.searchByTitle')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD]"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-black/50 border border-white/10 text-white px-4 py-2 rounded-lg focus:border-[#23F7DD] focus:outline-none"
            >
              <option value="">{t('publicProjects.allTypes')}</option>
              <option value="assembly">{t('admin.assembly')}</option>
              <option value="disassembly">{t('admin.disassembly')}</option>
              <option value="both">{t('admin.both')}</option>
            </select>

            <div className="text-[#94A3B8]">
              {filteredProjects.length} {t('nav.projects').toLowerCase()}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="px-6 md:px-12 py-12">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-12 h-12 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-24">
              <Building2 className="w-16 h-16 text-[#475569] mx-auto mb-6" />
              <h3 className="text-2xl font-['Unbounded'] font-bold text-white mb-4">
                {t('publicProjects.noProjectsAvailable')}
              </h3>
              <p className="text-[#94A3B8] mb-8 max-w-md mx-auto">
                {searchQuery 
                  ? t('publicProjects.tryDifferentSearch')
                  : t('publicProjects.checkBackSoon')}
              </p>
              <Link to="/register?type=client">
                <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold uppercase tracking-wider">
                  {t('publicProjects.postYourProject')}
                </Button>
              </Link>
            </div>
          ) : (
                      <div className="space-y-4">
              {filteredProjects.map((project) => (
                <Card 
                  key={project.project_id} 
                  className={`bg-[#0A0A0A] border-white/10 transition-all duration-300 overflow-hidden ${
                    canSeePrices ? 'hover:border-[#23F7DD]/50 group' : ''
                  }`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Project Image */}
                    <div className="relative w-full md:w-72 h-48 md:h-auto bg-gradient-to-br from-[#23F7DD]/10 to-[#4F46E5]/10 flex-shrink-0">
                      {project.photos && project.photos.length > 0 ? (
                        <img 
                          src={typeof project.photos[0] === 'string' ? project.photos[0] : project.photos[0]?.url}
                          alt={project.title}
                          className={`w-full h-full object-cover transition-transform duration-500 ${canSeePrices ? 'group-hover:scale-105' : ''}`}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center min-h-[180px]">
                          <Building2 className="w-16 h-16 text-[#23F7DD]/30" />
                        </div>
                      )}
                      
                      {/* Type Badge */}
                      <div className="absolute top-4 left-4 flex gap-2">
                        {project.project_type === "both" ? (
                          <>
                            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#23F7DD] text-[#050505]">
                              assembly
                            </span>
                            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#FF5F1F] text-white">
                              disassembly
                            </span>
                          </>
                        ) : (
                          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                            project.project_type === "assembly" 
                              ? "bg-[#23F7DD] text-[#050505]"
                              : "bg-[#FF5F1F] text-white"
                          }`}>
                            {project.project_type}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Content */}
                    <CardContent className="flex-1 p-6 flex flex-col md:flex-row gap-6">
                      {/* Main Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <h3 className="text-xl font-['Unbounded'] font-semibold text-white">
                            {project.title}
                          </h3>
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30 flex-shrink-0">
                            {project.status === "quoted" ? t('common.hasQuotes') : t('common.open')}
                          </span>
                        </div>

                        {/* Description - only for authenticated workers/TL */}
                        {canSeePrices && project.description && (
                          <p className="text-[#94A3B8] text-sm mb-4 line-clamp-2">
                            {project.description}
                          </p>
                        )}
                        
                        {/* Message for unauthenticated users */}
                        {!canSeePrices && (
                          <p className="text-[#94A3B8] text-sm mb-4 italic">
                            Logg inn som stillasarbeider for å se prosjektdetaljer
                          </p>
                        )}

                        <div className="flex flex-wrap gap-4 text-sm">
                          <div className="flex items-center gap-2 text-[#94A3B8]">
                            <MapPin className="w-4 h-4 text-[#23F7DD]" />
                            <span>{project.location_city}</span>
                          </div>
                          
                          {project.start_date && (
                            <div className="flex items-center gap-2 text-[#94A3B8]">
                              <Calendar className="w-4 h-4 text-[#23F7DD]" />
                              <span>{t('projects.starts')} {project.start_date}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 text-[#94A3B8]">
                            <Users className="w-4 h-4 text-[#23F7DD]" />
                            <span>{project.required_crew_size} {t('nav.workers').toLowerCase()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Price & Action */}
                      <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-center gap-4 pt-4 md:pt-0 border-t md:border-t-0 md:border-l border-white/10 md:pl-6">
                        {canSeePrices ? (
                          <>
                            <div className="text-left md:text-right">
                              <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">{t('admin.budget')}</p>
                              <p className="text-xl font-['Unbounded'] font-bold text-[#23F7DD]">
                                {formatPrice(project.budget_min, project.budget_max)}
                              </p>
                            </div>
                            <Link to={`/team-leader/browse-projects/${project.project_id}`}>
                              <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold">
                                {t('browse.viewDetails')}
                                <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          </>
                        ) : (
                          <>
                            <div className="text-left md:text-right">
                              <div className="flex items-center gap-2 text-[#94A3B8]">
                                <Lock className="w-4 h-4" />
                                <span className="text-xs uppercase tracking-wider">Kun for registrerte</span>
                              </div>
                            </div>
                            <Link to="/login">
                              <Button variant="outline" className="border-[#23F7DD] text-[#23F7DD] hover:bg-[#23F7DD]/10 font-bold">
                                Logg inn
                                <ChevronRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 md:px-12 py-16 bg-gradient-to-b from-[#0A0A0A] to-[#050505] border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-full mb-6">
            <Zap className="w-4 h-4 text-[#23F7DD]" />
            <span className="text-[#23F7DD] text-sm font-medium">{t('publicProjects.needScaffoldingServices')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-['Unbounded'] font-bold text-white mb-4">
            {t('publicProjects.postProjectToday')}
          </h2>
          <p className="text-xl text-[#94A3B8] mb-8">
            {t('publicProjects.getQuotes')}
          </p>
          <Link to="/register?type=client">
            <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] px-8 py-6 text-lg font-bold uppercase tracking-wider">
              {t('publicProjects.getStarted')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 md:px-12 py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div className="px-2 py-0.5 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
                <span className="text-[#23F7DD] font-bold text-sm font-['Unbounded']">All</span>
              </div>
              <span className="text-lg font-bold font-['Unbounded'] neon-text-subtle">Stillas</span>
            </div>
            <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#23F7DD]/10 border border-[#23F7DD]/20 rounded-full">
              <span className="text-[#23F7DD] text-[10px]">⚡</span>
              <span className="text-[10px] text-[#94A3B8]">Powered by</span>
              <span className="text-[10px] text-[#23F7DD]">Doserne AS</span>
            </div>
          </div>
          <span className="text-[#94A3B8] text-sm">© 2026 Doserne AS. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
